import classnames from 'classnames'
import throttle from 'lodash.throttle'
import * as React from 'react'

import tailwindColors from '../../../../../../config/colors'
import * as hoverChart from '../../../shared/hover-chart'
import TilesStore from '../../stores/tilesStore'
import Sparkline from '../CanvasSparkline'

interface TileWithBorderProps {
  ticker: string
  showSparkline: boolean
  hoverOffsetX: number
  hoverOffsetY: number
  colorScale: (d: number) => string
}

function getTileState(ticker: string, prevWidth = 0) {
  var tile = TilesStore.getTile(ticker)
  var group = TilesStore.getTilesGroup(tile.ticker).toLowerCase()

  return {
    ...tile,
    group,
    width: prevWidth,
  }
}

export class TileWithBorder extends React.Component<React.PropsWithChildren<TileWithBorderProps>> {
  state = getTileState(this.props.ticker)

  resizeObserver?: ResizeObserver
  containerRef = React.createRef<HTMLDivElement>()

  componentDidMount() {
    TilesStore.addChangeListener(this._onChange)

    // Resize sparkline on window resize
    const container = this.containerRef.current
    if (container && this.state.sparkline) {
      this.setState({ width: container.getBoundingClientRect().width }, () => {
        this.resizeObserver = new ResizeObserver(
          throttle(([entry]: ResizeObserverEntry[]) => {
            this.setState({ width: entry.contentRect.width })
          }, 100)
        )
        this.resizeObserver.observe(container)
      })
    }
  }

  componentWillUnmount() {
    TilesStore.removeChangeListener(this._onChange)
    this.resizeObserver?.disconnect()
  }

  _onChange = () => {
    this.setState(getTileState(this.props.ticker, this.state.width))
  }

  render() {
    const { colorScale, children, showSparkline, hoverOffsetX, hoverOffsetY } = this.props
    var { ticker, width, label, last, prevClose, change, high, low, sparkline, sparklineDateChanges } = this.state

    var format = (n: number) => (n >= 0 ? '+' : '') + n.toFixed(2) + '%'
    var places = last > 6 ? 2 : 4
    var round = (n: number, plusSign?: string) =>
      (n >= 0 ? plusSign || '' : '') + (+(Math.round(Number(n + 'e+' + places)) + 'e-' + places)).toFixed(places)

    var range = (high - low) * 0.1
    var highlightHigh = last > high - range
    var highlightLow = last < low + range
    const backgroundColor = colorScale(change)

    const chartImage = hoverChart.getHoverChartImg(
      '@' + ticker.toLowerCase(),
      false,
      FinvizSettings.hoverCharts?.futures?.timeframe,
      FinvizSettings.hoverCharts?.futures?.dateRange
    )

    const boxover = `cssbody=[hoverchart] cssheader=[tabchrthdr] body=[${chartImage.img}] offsetx=[${hoverOffsetX}] offsety=[${hoverOffsetY}] delay=[100]`

    if (showSparkline) {
      return (
        <a
          className="font-3xs flex w-full overflow-hidden font-sans text-3xs font-medium"
          href={'/futures_charts.ashx?p=d1&t=' + ticker}
          data-boxover={boxover}
        >
          {children}
          <div className="w-1 shrink-0 xl:w-1.5" style={{ backgroundColor }} />
          <div
            ref={this.containerRef}
            className="w-full overflow-hidden"
            style={{ backgroundColor: `${backgroundColor}14` }}
          >
            <div className="w-full whitespace-nowrap p-1 xl:p-1.5 xl:pb-1">
              <div className="flex w-full items-baseline justify-between overflow-hidden">
                <div className="truncate uppercase leading-none text-gray-600 xl:text-2xs xl:leading-none">{label}</div>
                <div
                  className={classnames('shrink-0 pl-1 leading-none', {
                    'text-green-500': last - prevClose >= 0,
                    'text-red-500': last - prevClose < 0,
                  })}
                >
                  {round(last - prevClose, '+')}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs font-bold xl:text-base">{round(last)}</div>
                <div
                  className={classnames('pl-1 leading-none', {
                    'text-green-500': change >= 0,
                    'text-red-500': change < 0,
                  })}
                >
                  ({format(change)})
                </div>
              </div>
            </div>
            <div className="w-full overflow-hidden">
              {showSparkline && (
                <Sparkline
                  key={width}
                  width={Math.ceil(width)}
                  height={40}
                  data={sparkline}
                  dateChanges={sparklineDateChanges}
                  change={change}
                  changeDomain={1.3}
                  changeOpacity={false}
                  lineColor={tailwindColors.green[500]}
                  textColor={tailwindColors.gray[500]}
                />
              )}
            </div>
          </div>
        </a>
      )
    }

    return (
      <a
        className="flex w-full font-sans text-3xs font-medium"
        href={'/futures_charts.ashx?p=d1&t=' + ticker}
        data-boxover={boxover}
      >
        {children}
        <div className="w-1 shrink-0 xl:w-1.5" style={{ backgroundColor }} />
        <div className="flex-1 p-1 xl:p-1.5 xl:pb-1" style={{ backgroundColor: `${backgroundColor}14` }}>
          <div className="uppercase leading-none text-gray-600 xl:text-2xs xl:leading-none">{label}</div>
          <div className="text-xl text-gray-800 font-bold xl:text-2xl">{round(last)}</div>
          <div className="tabular-nums leading-none">
            <div className="flex items-center justify-between">
              <div
                className={classnames('-mx-0.5 rounded-sm p-0.5 text-gray-600 xl:text-2xs xl:leading-3', {
                  'bg-yellow-200': highlightHigh,
                })}
              >
                <span
                  className={classnames('inline-block w-2.5 pr-0.5 font-normal', {
                    'text-gray-500': !highlightHigh,
                    'text-gray-600': highlightHigh,
                  })}
                >
                  H
                </span>
                <span
                  className={classnames({
                    'text-gray-600': !highlightHigh,
                    'text-gray-800': highlightHigh,
                  })}
                >
                  {round(high)}
                </span>
              </div>
              <div
                className={classnames({
                  'text-green-500': last - prevClose >= 0,
                  'text-red-500': last - prevClose < 0,
                })}
              >
                {round(last - prevClose, '+')}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div
                className={classnames('-mx-0.5 rounded-sm p-0.5 text-gray-600 xl:text-2xs xl:leading-3', {
                  'bg-yellow-200': highlightLow,
                })}
              >
                <span
                  className={classnames('inline-block w-2.5 pr-0.5 font-normal', {
                    'text-gray-500': !highlightLow,
                    'text-gray-600': highlightLow,
                  })}
                >
                  L
                </span>
                <span
                  className={classnames('leading-none', {
                    'text-gray-600': !highlightLow,
                    'text-gray-800': highlightLow,
                  })}
                >
                  {round(low)}
                </span>
              </div>
              <div
                className={classnames({
                  'text-green-500': change >= 0,
                  'text-red-500': change < 0,
                })}
              >
                {format(change)}
              </div>
            </div>
          </div>
        </div>
      </a>
    )
  }
}
